import React, { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { LockKeyhole, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { API_BASE_URL } from '../services/api';

const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const role = searchParams.get('role') || 'student';
  const isTeacher = role === 'teacher';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const endpoint = isTeacher
    ? `${API_BASE_URL}/tutors/reset-password/${token}`
    : `${API_BASE_URL}/auth/reset-password/${token}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      return setError('Password must be at least 6 characters.');
    }
    if (password !== confirmPassword) {
      return setError('Passwords do not match.');
    }
    setLoading(true);
    try {
      const res = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to reset password.');
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const accentColor = isTeacher ? 'text-purple-400' : 'text-accentBlue';
  const accentBg = isTeacher ? 'bg-purple-500/10 border-purple-500/20' : 'bg-accentBlue/10 border-accentBlue/20';
  const accentBtn = isTeacher
    ? 'bg-purple-600 hover:bg-purple-500 text-white'
    : 'bg-accentBlue text-black hover:scale-[1.02]';

  if (success) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-200px)] px-6 py-12">
        <div className="w-full max-w-md bg-white/5 border border-white/10 p-8 rounded-[40px] backdrop-blur-xl text-center space-y-6">
          <div className="w-16 h-16 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle size={32} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">Password Updated!</h2>
            <p className="text-gray-400 text-sm">Your password has been reset successfully.</p>
          </div>
          <button
            onClick={() => navigate(isTeacher ? '/teacher-login' : '/login')}
            className={`w-full py-3.5 rounded-2xl font-bold transition-all ${accentBtn}`}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-200px)] px-6 py-12">
      <div className="w-full max-w-md bg-white/5 border border-white/10 p-8 rounded-[40px] backdrop-blur-xl">
        <div className="text-center mb-8">
          <div className={`inline-flex items-center gap-2 px-4 py-1.5 ${accentBg} border rounded-full ${accentColor} text-xs font-bold uppercase tracking-wider mb-4`}>
            {isTeacher ? 'Teacher' : 'Student'} · Reset Password
          </div>
          <h2 className="text-3xl font-bold text-white flex items-center justify-center gap-2">
            <LockKeyhole size={28} className={accentColor} /> New Password
          </h2>
          <p className="text-gray-400 text-sm mt-2">Enter your new password below</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-400 p-3 rounded-xl mb-6 text-sm text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">New Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-darkBg border border-white/10 p-3.5 pr-12 rounded-xl focus:border-accentBlue focus:ring-2 focus:ring-accentBlue/20 outline-none text-sm transition-all"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Confirm New Password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full bg-darkBg border border-white/10 p-3.5 rounded-xl focus:border-accentBlue focus:ring-2 focus:ring-accentBlue/20 outline-none text-sm transition-all"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3.5 rounded-2xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${accentBtn}`}
          >
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to={isTeacher ? '/teacher-login' : '/login'}
            className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
          >
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
